import type { PillarPageContent } from "./types";

/**
 * Category pages. Their job is positioning and routing: state the problem the
 * pillar exists to solve, then hand off to the product pages. They deliberately
 * do not repeat product detail.
 */
export const PILLAR_PAGES: PillarPageContent[] = [
  {
    key: "portfolio",
    path: "/products",
    eyebrow: "Portfolio",
    headline: "Security and compliance for AI that acts on its own",
    lede:
      "Two pillars on one foundation: defend agents at runtime, and prove they stay inside the rules.",
    problems: [
      {
        title: "Agents act before anyone reviews",
        body: "Autonomy removes the human checkpoint that most security controls quietly depend on.",
      },
      {
        title: "Regulation arrived first this time",
        body: "The EU AI Act, ISO 42001 and NIST AI RMF landed while most AI estates were still undocumented.",
      },
      {
        title: "Evidence has to survive scrutiny",
        body: "Auditors and regulators want a record they can verify, not a screenshot someone has to vouch for.",
      },
    ],
    products: [
      "cerberus",
      "argus",
      "mimir",
      "guardian",
      "tracelock",
      "sentience",
      "bifrost",
    ],
    solutions: [
      "/secure-agentic-ai",
      "/govern-ai",
      "/audit-ready",
      "/ai-compliance",
    ],
    close: {
      heading: "Start where the risk is",
      body: "Most teams start with the runtime or the evidence. Both paths converge on the same governed foundation.",
      ctas: [
        { label: "Request a briefing", href: "/contact?topic=portfolio" },
        { label: "See a live demo", href: "/demos" },
      ],
    },
  },
  {
    key: "ai-security",
    path: "/ai-security",
    eyebrow: "AI Security",
    headline: "Cybersecurity for the agentic era",
    lede:
      "Legacy assurance was built for software that waits to be told what to do. These products are built for systems that act on their own — defending the tool boundary, attacking it first, detecting poisoned memory, and keeping every agent inside human authority.",
    problems: [
      {
        title: "The attack needs no exploit",
        body: "Privileged access, untrusted content and an outbound call are enough. Every capable agent has all three.",
      },
      {
        title: "Prompt filtering guesses",
        body: "Intent detection on text is a losing game across languages and encodings. The action is the thing worth judging.",
      },
      {
        title: "Memory persists",
        body: "A poisoned memory keeps working long after the injected page is gone, and nothing in the transcript looks wrong.",
      },
    ],
    products: ["cerberus", "argus", "mimir", "guardian"],
    solutions: ["/secure-agentic-ai", "/govern-ai"],
    close: {
      heading: "See it stop a live attack",
      body: "The validation harness, the traces and the blocked exfiltration are all published.",
      ctas: [
        { label: "Explore Cerberus", href: "/cerberus" },
        { label: "Read the research", href: "/research" },
      ],
    },
  },
  {
    key: "governance",
    path: "/governance",
    eyebrow: "Governance & Compliance",
    headline: "Continuous trust, immutable evidence",
    lede:
      "Prove control effectiveness continuously instead of annually, and hand auditors a record they can verify rather than a folder they have to believe.",
    problems: [
      {
        title: "Annual evidence is already stale",
        body: "A point-in-time screenshot describes a system that has since changed.",
      },
      {
        title: "Frameworks multiply the same work",
        body: "The same control gets re-tested once per framework instead of evaluated once and mapped.",
      },
      {
        title: "AI is not in the inventory",
        body: "You cannot govern models and agents you have not discovered, and the new regulations assume you have.",
      },
    ],
    products: ["tracelock", "sentience", "bifrost"],
    solutions: ["/audit-ready", "/ai-compliance", "/third-party-risk"],
    close: {
      heading: "Prove the controls, execute the obligations, answer the review",
      body: "TraceLock proves your controls hold, Sentience watches regulation and executes what it demands, and Bifrost governs and verifies the exchange of that evidence across organizational boundaries.",
      ctas: [
        { label: "Explore TraceLock", href: "/tracelock" },
        { label: "Explore Sentience", href: "/sentience" },
      ],
    },
  },
];
